import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandMark } from "../components/Logo";

function Shell({ title, updated, children }) {
  return (
    <div className="min-h-screen grain">
      <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-3xl mx-auto h-full px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark />
            <span className="font-display font-bold text-xl">Facio Doc</span>
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-indigo-600 flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Home</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="font-display text-4xl font-bold tracking-tight">{title}</h1>
        <p className="text-slate-400 font-mono text-sm mt-2">Last updated: {updated}</p>
        <div className="prose prose-slate mt-8 max-w-none text-slate-700 leading-relaxed space-y-5 [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-2 [&_h2]:text-slate-900 [&_h3]:font-display [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-1 [&_h3]:text-slate-900 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1">
          {children}
        </div>
      </main>
    </div>
  );
}

export function Terms() {
  return (
    <Shell title="Terms of Service" updated="October 2026">
      <h2>1. Overview</h2>
      <p>Facio Doc ("Facio Doc", "we", "us", "the Service") provides tools to create, convert, edit, and manage PDF documents. By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.</p>

      <h2>2. Eligibility</h2>
      <p>You must be at least 18 years old or the age of majority in your jurisdiction. You may not use the Service if you are barred under applicable law or under Google/Stripe policies.</p>

      <h2>3. Accounts &amp; Authentication</h2>
      <p>You sign in exclusively through Google Sign In. You are responsible for maintaining the security of your Google account. We do not offer password-based login, manual account recovery, or alternative authentication methods. If you lose access to your Google account, we cannot guarantee recovery of your Facio Doc account or documents.</p>

      <h2>4. Credits, Pricing &amp; Payments</h2>
      <h3>4.1 Credit System</h3>
      <p>Facio Doc operates on a pay as you go credit model. Credits are consumed per export or conversion based on tool type:</p>
      <ul>
        <li>Basic tools: 1 credit</li>
        <li>Advanced tools (OCR, Office → PDF, PDF → Office): 3 credits</li>
        <li>Large-document surcharge: applied when file size exceeds posted limits</li>
        <li>Workspace/team exports: +1 credit premium</li>
      </ul>
      <p>Credit cost is always shown before you export.</p>
      <h3>4.2 Purchasing Credits</h3>
      <p>Credits are purchased through Stripe. All prices are displayed before checkout.</p>
      <h3>4.3 Refunds</h3>
      <p>Credits are only deducted after you review and approve the document output. Because you always see the result before confirming the export, credits that have been used are non refundable. Unused credits may be refundable only where required by law. If a document fails to generate or the Service produces an error before approval, no credit is deducted.</p>
      <h3>4.4 Pricing Changes</h3>
      <p>We may update pricing or credit costs. Changes apply to future purchases only.</p>
      <h3>4.5 Credit Expiration</h3>
      <p>Credits do not expire as long as your account remains active. An account is considered active when you sign in or use any feature of the Service at least once every 24 months.</p>
      <h3>4.6 Account Closure Due to Inactivity</h3>
      <p>If your account remains completely inactive for 24 consecutive months, we may close your account and permanently delete any unused credits. Before closing an inactive account, we will send a notice to your account email, giving you an opportunity to sign in and keep your account active. Account closure includes removal of stored documents, workspace membership, and unused credits. Once an account is closed due to inactivity, it cannot be restored.</p>

      <h2>5. Workspaces</h2>
      <p>Workspace owners fund a shared credit pool. Credits consumed by workspace members are deducted from the owner's pool. Documents created inside a workspace are visible to all workspace members. Owners are responsible for managing invitations and removals. We are not responsible for data shared within a workspace.</p>

      <h2>6. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Upload illegal, harmful, infringing, or malicious content</li>
        <li>Upload content you do not have rights to process</li>
        <li>Attempt to bypass rate limits or automate high-volume extraction</li>
        <li>Reverse engineer, scrape, or interfere with the Service</li>
        <li>Use the Service for activities that violate applicable laws</li>
      </ul>
      <p>We may suspend or terminate accounts violating these rules.</p>

      <h2>7. Your Content</h2>
      <p>You retain ownership of all files you upload or create. You grant us a limited, revocable license to:</p>
      <ul>
        <li>process your files</li>
        <li>store them temporarily</li>
        <li>generate previews</li>
        <li>perform conversions</li>
        <li>maintain workspace functionality</li>
      </ul>
      <p>This license exists solely to provide the Service. You are responsible for the legality of your content.</p>

      <h2>8. File Storage, Retention &amp; Deletion</h2>
      <p>Facio Doc is designed for short term processing, not long term storage. Files you upload or generate are stored only for the purpose of processing and providing the Service. You may delete your files at any time from within your account. If you do not delete your files manually, they are automatically deleted within 24 hours of upload or creation. Temporary processing copies are also deleted within this 24 hour window. Deleted files may persist briefly in system backups, but are permanently removed within a reasonable period. We do not use your files to train AI models or for any purpose other than providing the Service. This 24 hour retention window acts as a safety net in case you need to re download or correct a file, but Facio Doc is not a storage service.</p>

      <h2>9. Third Party Services</h2>
      <p>Facio Doc relies on:</p>
      <ul>
        <li>Google — authentication and optional Drive integration</li>
        <li>Stripe — payments</li>
        <li>Hosting/storage providers</li>
        <li>Conversion engines</li>
      </ul>
      <p>Your use of these features is subject to their respective terms and policies. We are not responsible for outages or data handling practices of third party providers.</p>

      <h2>10. Availability &amp; Performance</h2>
      <p>The Service is provided "as is". Conversion fidelity — especially OCR, Office conversions, and reverse conversions — may vary. We do not guarantee perfect formatting, accuracy, or preservation of layout.</p>

      <h2>11. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law:</p>
      <ul>
        <li>We are not liable for indirect, incidental, special, or consequential damages</li>
        <li>We are not liable for loss of data, profits, business, or goodwill</li>
        <li>Our total liability for any claim is limited to the amount you paid in the past 12 months</li>
      </ul>
      <p>Some jurisdictions do not allow these limitations; in those cases, the limitations apply to the fullest extent permitted.</p>

      <h2>12. International Users</h2>
      <p>Users outside the United States may have additional consumer or privacy rights under local laws. We honor these rights to the extent required by applicable law.</p>

      <h2>13. Governing Law &amp; Jurisdiction</h2>
      <p>These Terms are governed by the laws of the State of Illinois, United States, without regard to conflict of law principles. If you reside outside the United States, you agree that any dispute will be resolved exclusively in the courts located in Cook County, Illinois.</p>

      <h2>14. Termination</h2>
      <p>You may stop using the Service at any time. We may suspend or terminate your account if:</p>
      <ul>
        <li>you violate these Terms</li>
        <li>your payment activity is fraudulent</li>
        <li>your usage threatens system stability</li>
        <li>required third party services become unavailable</li>
      </ul>

      <h2>15. Changes to Terms</h2>
      <p>We may update these Terms. If changes are material, we will notify you via email or in-app notice. Continued use of the Service constitutes acceptance of updated Terms.</p>

      <h2>16. Contact</h2>
      <p>Questions? Email <a href="mailto:support@faciodoc.com">support@faciodoc.com</a>.</p>
    </Shell>
  );
}

export function Privacy() {
  return (
    <Shell title="Privacy Policy" updated="October 2026">
      <h2>1. Overview</h2>
      <p>This Privacy Policy explains what data we collect, how we use it, and your rights.</p>

      <h2>2. Data We Collect</h2>
      <h3>2.1 Account Data</h3>
      <ul>
        <li>Google account ID</li>
        <li>Email address</li>
        <li>Name (from Google profile)</li>
        <li>Workspace membership information</li>
      </ul>
      <p>We do not collect passwords.</p>
      <h3>2.2 Payment Data</h3>
      <p>Payments are processed by Stripe. We do not store full credit card numbers.</p>
      <h3>2.3 File Data</h3>
      <ul>
        <li>Files you upload</li>
        <li>Files you create</li>
        <li>Conversion outputs</li>
        <li>Temporary processing copies</li>
      </ul>
      <p>Files are retained for a maximum of 24 hours, unless you delete them earlier.</p>
      <h3>2.4 Usage Data</h3>
      <ul>
        <li>Tool usage</li>
        <li>Credit consumption</li>
        <li>Device/browser information</li>
        <li>Error logs</li>
        <li>Workspace activity</li>
      </ul>

      <h2>3. How We Use Your Data</h2>
      <p>We use your data to:</p>
      <ul>
        <li>authenticate your account</li>
        <li>process and convert files</li>
        <li>maintain workspaces</li>
        <li>display your documents</li>
        <li>provide customer support</li>
        <li>improve reliability and performance</li>
        <li>comply with legal obligations</li>
      </ul>
      <p>We do not sell your data. We do not use your content to train AI models.</p>

      <h2>4. Legal Basis for Processing (EU/EEA, UK, Brazil)</h2>
      <p>For users in regions with data protection laws (GDPR, UK GDPR, LGPD), we process personal data under:</p>
      <ul>
        <li>Contract performance — providing the Service</li>
        <li>Legitimate interests — improving reliability, preventing abuse</li>
        <li>Consent — Google Drive integration, optional marketing</li>
        <li>Legal obligation — tax, fraud, regulatory compliance</li>
      </ul>

      <h2>5. File Processing, Retention &amp; Deletion</h2>
      <p>Files are stored only for processing and providing the Service. You may delete your files at any time. All files are automatically deleted within 24 hours of upload or creation. Temporary processing copies are also deleted within this 24 hour window. Deleted files may persist briefly in backups but are permanently removed within a reasonable period. We do not provide long term storage and do not retain files beyond this window.</p>

      <h2>6. Sharing of Data</h2>
      <p>We share data only with:</p>
      <ul>
        <li>Google (authentication, optional Drive access)</li>
        <li>Stripe (payments)</li>
        <li>Hosting/storage providers</li>
        <li>Conversion engines</li>
      </ul>
      <p>We do not share your data with advertisers.</p>

      <h2>7. International Data Transfers</h2>
      <p>Your data may be processed in the United States or other countries where our service providers operate. By using the Service, you consent to these transfers as permitted by applicable law. Where required (EU/EEA, UK, Brazil), transfers rely on appropriate safeguards such as Standard Contractual Clauses or equivalent mechanisms.</p>

      <h2>8. Security</h2>
      <p>We use industry-standard security measures, including:</p>
      <ul>
        <li>encrypted transport (HTTPS)</li>
        <li>secure hosting</li>
        <li>access controls</li>
        <li>limited employee access</li>
      </ul>
      <p>No system is perfectly secure; use caution with sensitive documents.</p>

      <h2>9. Your Rights</h2>
      <p>Depending on your jurisdiction, you may have rights to:</p>
      <ul>
        <li>access your data</li>
        <li>delete your data</li>
        <li>correct inaccurate data</li>
        <li>request export of your data</li>
        <li>restrict certain processing</li>
      </ul>
      <p>Contact <a href="mailto:support@faciodoc.com">support@faciodoc.com</a> to exercise these rights.</p>

      <h2>10. Children's Privacy</h2>
      <p>Facio Doc is not intended for children under 13 (or under the age of digital consent in your region). We do not knowingly collect data from children.</p>

      <h2>11. Changes to This Policy</h2>
      <p>We may update this Privacy Policy. Material changes will be communicated via email or in-app notice.</p>

      <h2>12. Contact</h2>
      <p>Questions? Email <a href="mailto:support@faciodoc.com">support@faciodoc.com</a>.</p>
    </Shell>
  );
}
